// @ts-nocheck
import React, { useState, useEffect } from 'react';

interface Props {
  isOpen: boolean;
  username: string;
  totalPosts: number;
  step: 'linking' | 'importing' | 'done' | 'error';
  shopId?: string;
  error?: string;
}

const STEP_CONFIG = {
  linking: {
    title: 'Setting up shop...',
    subtitle: 'Configuring Instagram sync',
    color: 'text-purple-600',
    bg: 'bg-purple-500',
  },
  importing: {
    title: 'Importing posts...',
    subtitle: 'Writing posts to shop',
    color: 'text-blue-600',
    bg: 'bg-blue-500',
  },
  done: {
    title: 'Import complete!',
    subtitle: 'All posts have been queued for processing',
    color: 'text-green-600',
    bg: 'bg-green-500',
  },
  error: {
    title: 'Import failed',
    subtitle: 'Something went wrong',
    color: 'text-red-600',
    bg: 'bg-red-500',
  },
};

const ImportProgressModal: React.FC<Props> = ({ isOpen, username, totalPosts, step, shopId, error }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (step === 'linking') setProgress(15);
    if (step === 'importing') {
      setProgress(30);
      const timer = setInterval(() => {
        setProgress(prev => Math.min(prev + 5, 85));
      }, 300);
      return () => clearInterval(timer);
    }
    if (step === 'done') setProgress(100);
    if (step === 'error') setProgress(0);
  }, [step]);

  if (!isOpen) return null;

  const config = STEP_CONFIG[step];
  const isDone = step === 'done';
  const isError = step === 'error';

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl">
        {/* Icon area */}
        <div className="pt-8 pb-4 flex justify-center">
          {isDone ? (
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          ) : isError ? (
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          ) : (
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center">
                <svg className="w-6 h-6 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <svg className="absolute inset-0 w-16 h-16 animate-spin" style={{ animationDuration: '2s' }} viewBox="0 0 64 64">
                <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(147,51,234,0.15)" strokeWidth="3" />
                <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(147,51,234,0.5)" strokeWidth="3" strokeDasharray="44 132" strokeLinecap="round" />
              </svg>
            </div>
          )}
        </div>

        {/* Text */}
        <div className="text-center px-6 pb-3">
          <h3 className={`text-lg font-bold ${config.color}`}>{config.title}</h3>
          <p className="text-sm text-gray-500 mt-1">{config.subtitle}</p>
        </div>

        {/* Progress bar */}
        {!isError && (
          <div className="px-6 pb-4">
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ease-out ${config.bg}`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Details */}
        <div className="px-6 pb-6 space-y-2">
          <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
            <span className="text-xs text-gray-500">Account</span>
            <span className="text-xs font-bold text-gray-700">@{username}</span>
          </div>
          <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
            <span className="text-xs text-gray-500">Posts</span>
            <span className="text-xs font-bold text-gray-700">{totalPosts}</span>
          </div>
          {shopId && (
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
              <span className="text-xs text-gray-500">Shop ID</span>
              <span className="text-xs font-mono font-bold text-gray-700">{shopId}</span>
            </div>
          )}
          {isError && error && (
            <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg">
              <p className="text-xs text-red-600">{error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportProgressModal;
